import axios from "axios";

export class BaseClient {
  private readonly token: string;
  private readonly baseUrl: string;

  public constructor(token: string) {
    this.token = token;
    const baseUrl = process.env.REACT_APP_BASE_API_URL ?? "";
    if (!baseUrl) {
      throw new Error("REACT_APP_BASE_API_URL is not configured.");
    }
    this.baseUrl = baseUrl;
  }

  /**
   * Make a request to the /me endpoint.
   */
  public async meAsync(): Promise<MeResponse> {
    try {
      const response = await axios.get<MeResponse>(
        `${this.baseUrl}/v2/accounts/me?include_extended=true&attributes=licensing_accounts,is_using_rbac`,
        {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        }
      );
      // Ensure email has a valid value, fallback to login if missing
      const email = response.data.email?.trim() || response.data.login;
      return { ...response.data, email };
    } catch (error) {
      throw new Error(
        `Failed to fetch user data from /me endpoint: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  /**
   * Request to the api.qa.base.cropwise.com/v2/licensing/accounts/{workspaceId} endpoint.
   *
   * @param workspaceId The Base workspace to get information.
   */
  public async getPlansByWorkspaceId(
    workspaceId: string | undefined
  ): Promise<ICropwisePlans> {
    const response = await axios.get<ICropwisePlans>(
      `${this.baseUrl}/v2/licensing/accounts/${workspaceId ?? ""}`,
      {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      }
    );

    return response.data;
  }

  /**
   * get property details for the given propertyId
   * @param propertyId The ID of the property to fetch details for.
   * @returns The details of the specified property.
   */
  public async getPropertyDetails(propertyId: string): Promise<Property> {
    const response = await axios.get<Property>(
      `${this.baseUrl}/v2/properties/${propertyId}?include_extended=true`,
      {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      }
    );

    return response.data;
  }

  /**
   * Verify which protocol operations the current user is allowed to perform on
   * a workspace and on one of its orgs.
   *
   * @param workspaceId The workspace the operations are verified against.
   * @param orgId The org the operations are verified against.
   * @returns The operations allowed for the given workspace/org.
   */
  public async getPermissions(
    workspaceId: string,
    orgId: string
  ): Promise<PermissionsResponse> {
    const payload = {
      operations: [
        { action: "protocol:read", resource: `crn:workspace:${workspaceId}` },
        { action: "protocol:write", resource: `crn:workspace:${workspaceId}` },
        { action: "protocol:delete", resource: `crn:workspace:${workspaceId}` },
        {
          action: "protocol:read",
          resource: `crn:workspace:${workspaceId}/org/${orgId}`,
        },
        {
          action: "protocol:write",
          resource: `crn:workspace:${workspaceId}/org/${orgId}`,
        },
        {
          action: "protocol:delete",
          resource: `crn:workspace:${workspaceId}/org/${orgId}`,
        },
      ],
    };

    const response = await axios.post<PermissionsResponse>(
      `${this.baseUrl}/v2/auth/verify`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      }
    );

    return response.data;
  }

  /**
   * Request to the /v2/workspaces/{workspaceId} endpoint.
   *
   * @param workspaceId The Base workspace to get information.
   */
  public async workspaceAsync(
    workspaceId: string
  ): Promise<ICropwiseBaseWorkspace> {
    const response = await axios.get<ICropwiseBaseWorkspace>(
      `${this.baseUrl}/v2/workspaces/${workspaceId}`,
      {
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      }
    );
    return response.data;
  }
}

export interface ICropwiseBaseWorkspace {
  name: string;
  created_at: string;
  updated_at: string;
  id: string;
  contracts: ICropwiseBaseContract[];
}

// Types for /me endpoint

export interface MeResponse {
  licensing_accounts: Array<{
    licensing_account_id: string;
    org_id: string;
    user_type: string | null;
    workspace_id: string;
    workspace_name: string | null;
  }>;
  json_extended_attributes: string | undefined;
  created_at: string;
  updated_at: string;
  last_updated_by_id: string;
  deleted: boolean;
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  authorities: Authority[];
  type: string;
  ssn: null;
  role: string;
  job_title: null;
  phone: string;
  locale: null;
  clock_format: null;
  date_format: null;
  tutorial_complete: null;
  opt_ins: OptIn[];
  pending_change_password: boolean;
  identity_provider: string;
  country_code: null;
  email_verified: boolean;
  is_using_rbac: boolean;
  login: string;
  auto_created_org_id: null;
  id: string;
  default_licensing_account_id: string;
  default_workspace_id: string;
  workspaceId?: string;
}

interface Authority {
  context: Context;
  permissions: Permission[];
  id: string;
}

enum Context {
  Organization = "ORGANIZATION",
  Property = "PROPERTY",
}

interface Permission {
  scope: string;
  name: string;
}

interface OptIn {
  type: string;
  accepted_on: string;
  id: string;
}

// Types for /v2/workspaces/{workspaceId}

interface ICropwiseBaseQuota {
  over_quota_threshold: number;
  unit: string;
  entity_type: string;
  usage: number;
  name: string;
  limit: number;
  threshold: number;
  value: number;
  key: string;
  entity: string;
  status: boolean;
}

export interface ICropwiseBaseEntitlement {
  app_id: string;
  updated_at: string;
  created_at: string;
  updated_by: string;
  created_by: string;
  key: string;
  id: string;
  name: string;
  deleted: boolean;
}

export interface ICropwiseBaseContract {
  quotas: ICropwiseBaseQuota[];
  created_at: string;
  sv: string;
  created_by?: string;
  plan: string;
  entitlements: ICropwiseBaseEntitlement[];
  updated_at: string;
  app_id: string;
  waiting_for_payment: boolean;
  deleted: boolean;
  updated_by?: string;
  notes: string;
  plan_name: string;
  end: string;
  id: string;
  start: string;
  workspace_id: string;
  licensing_account_id: string;
}

interface ICropwisePlans {
  data: unknown;
  name: string;
  created_at: string;
  updated_at: string;
  id: string;
  contracts: ICropwiseBaseContract[];
}

export interface ISimpleWorkspace {
  id: string;
  name: string;
  use_type: string;
  metadata: Record<string, unknown>;
  image_uri: string;
  created_by: string | null;
  updated_by: string | null;
  deleted: boolean;
  created_at: string;
  updated_at: string;
}
export interface IFilteredWorkspace {
  id: string;
  name: string;
  contracts: IFilteredWorkspaceContract[];
}
export interface IFilteredWorkspaceContract {
  app_id: string;
  plan_id: string;
  plan_name: string;
  is_active: boolean;
  start_date: string;
  end_date: string;
}

// Types for /v2/auth/verify

export interface PermissionOperation {
  resource: string;
  action: string;
}

export interface PermissionsResponse {
  allowed_operations?: PermissionOperation[];
}

export interface Property {
  id: string;
  address: string;
  city: string;
  country: string;
  name: string;
  org_id: string;
  place_id: string;
  reference_point: any; // This should be a GeoJson point
  root_region_id: string;
  state: string;
  time_zone: string;
  total_area: number;
  zip_code: string;
}
